import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Users, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Alternador, AreaSensivel, CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { ErroRequisicao, del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { data } from '../../nucleo/formato';
import { DIAS_SEMANA, type Canal, type Fila, type Horario } from './tipos';

const CANAIS: Canal[] = ['whatsapp', 'instagram', 'email', 'portal', 'site'];

interface Membro {
  id: string;
  nome: string;
}

const HORARIO_PADRAO: Horario = { dias: Object.fromEntries(['1', '2', '3', '4', '5'].map((d) => [d, [{ inicio: '08:00', fim: '18:00' }]])), feriados: [] };

/**
 * Trilha 5b (Digisac): horário de atendimento da fila. Cada dia com até 3 faixas (sem faixa = fechado) e os feriados. Os
 * campos de hora e data são os do núcleo (`CampoData`); nada de componente novo.
 */
export function EditorHorario({ horario, aoMudar, desabilitado }: { horario: Horario; aoMudar: (h: Horario) => void; desabilitado: boolean }) {
  const { t } = useTranslation('conversas');
  const [feriado, setFeriado] = useState('');
  const faixasDe = (d: (typeof DIAS_SEMANA)[number]) => horario.dias[d] ?? [];
  const mudarDia = (d: (typeof DIAS_SEMANA)[number], faixas: Horario['dias'][typeof d]) => aoMudar({ ...horario, dias: { ...horario.dias, [d]: faixas } });
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ul className="flex min-w-0 flex-col gap-2">
        {DIAS_SEMANA.map((d) => {
          const nome = t(`t5b.horario.dias.${d}`);
          const faixas = faixasDe(d);
          return (
            <li key={d} className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-borda bg-sup-2 px-3 py-2">
              <span className="flex min-w-0 items-center justify-between gap-2 text-sm">
                <span className="font-medium text-tinta-2">{nome}</span>
                {!faixas.length && <span className="text-xs text-tinta-3">{t('t5b.horario.fechado')}</span>}
              </span>
              {faixas.map((f, i) => (
                <span key={i} className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="min-w-0 flex-1 basis-28">
                    <CampoData type="time" rotulo={t('t5b.horario.inicio')} aria-label={t('t5b.horario.inicioDe', { n: i + 1, dia: nome })} disabled={desabilitado} value={f.inicio} onChange={(e) => mudarDia(d, faixas.map((x, j) => (j === i ? { ...x, inicio: e.target.value } : x)))} />
                  </span>
                  <span className="min-w-0 flex-1 basis-28">
                    <CampoData type="time" rotulo={t('t5b.horario.fim')} aria-label={t('t5b.horario.fimDe', { n: i + 1, dia: nome })} disabled={desabilitado} value={f.fim} onChange={(e) => mudarDia(d, faixas.map((x, j) => (j === i ? { ...x, fim: e.target.value } : x)))} />
                  </span>
                  <Botao variante="fantasma" tamanho="sm" disabled={desabilitado} aria-label={t('t5b.horario.removerFaixa', { n: i + 1, dia: nome })} icone={<X className="size-4" />} onClick={() => mudarDia(d, faixas.filter((_, j) => j !== i))} />
                </span>
              ))}
              {faixas.length < 3 && !desabilitado && (
                <Botao variante="fantasma" tamanho="sm" className="w-fit" icone={<Plus className="size-4" />} onClick={() => mudarDia(d, [...faixas, { inicio: faixas.length ? faixas[faixas.length - 1]!.fim : '08:00', fim: '18:00' }])}>
                  {t('t5b.horario.adicionarFaixa')}
                </Botao>
              )}
            </li>
          );
        })}
      </ul>
      <fieldset className="flex min-w-0 flex-col gap-1.5">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('t5b.horario.feriados')}</legend>
        {!!horario.feriados.length && (
          <span className="flex min-w-0 flex-wrap gap-1.5">
            {horario.feriados.map((f) => (
              <span key={f} className="flex items-center gap-1">
                <Selo>{data(f)}</Selo>
                <Botao variante="fantasma" tamanho="sm" disabled={desabilitado} aria-label={t('t5b.horario.removerFeriado', { data: data(f) })} icone={<X className="size-4" />} onClick={() => aoMudar({ ...horario, feriados: horario.feriados.filter((x) => x !== f) })} />
              </span>
            ))}
          </span>
        )}
        {!desabilitado && (
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 basis-40">
              <CampoData type="date" rotulo={t('t5b.horario.adicionarFeriado')} value={feriado} onChange={(e) => setFeriado(e.target.value)} />
            </span>
            <Botao
              tamanho="sm"
              icone={<Plus className="size-4" />}
              disabled={!/^\d{4}-\d{2}-\d{2}$/.test(feriado)}
              onClick={() => {
                if (!horario.feriados.includes(feriado)) aoMudar({ ...horario, feriados: [...horario.feriados, feriado].sort() });
                setFeriado('');
              }}
            >
              {t('t5b.horario.adicionarFeriado')}
            </Botao>
          </span>
        )}
      </fieldset>
    </div>
  );
}

/**
 * Fase 2 (W9, F1c): filas de atendimento. Cada fila diz os canais cuja conversa nova entra nela, a distribuição
 * (manual ou rodízio) e os membros. Quem não tem "ver todas as conversas" vê só as filas de que é membro e o que está sem
 * fila. Formulário no padrão das outras abas: gaveta, rótulos visíveis, caixa de seleção nativa e erro por campo.
 */
function FormFila({ fila, aoConcluir }: { fila: Fila | null; aoConcluir: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const podeEditar = s.pode('conversas.configurar');
  const [nome, setNome] = useState(fila?.nome ?? '');
  const [distribuicao, setDistribuicao] = useState<Fila['distribuicao']>(fila?.distribuicao ?? 'manual');
  const [canais, setCanais] = useState<Canal[]>(fila?.canais ?? []);
  const [membros, setMembros] = useState<string[]>(fila?.membros.map((m) => m.usuario_id) ?? []);
  const [ativa, setAtiva] = useState(fila?.ativa ?? true);
  // Trilha 5b: limite por atendente (menor carga), horário e mensagem fora do horário.
  const [limite, setLimite] = useState(fila?.limite_por_atendente ? String(fila.limite_por_atendente) : '');
  const [usarHorario, setUsarHorario] = useState(!!fila?.horario);
  const [horario, setHorario] = useState<Horario>(fila?.horario ?? HORARIO_PADRAO);
  const [mensagemFora, setMensagemFora] = useState(fila?.mensagem_fora_horario ?? '');
  // Trajetórias (item 4): oportunidade automática para lead novo, no funil escolhido (vazio = o padrão).
  const [oportunidadeAuto, setOportunidadeAuto] = useState(fila?.oportunidade_automatica ?? false);
  const [funilOp, setFunilOp] = useState(fila?.oportunidade_funil_id ?? '');
  const comPipeline = s.instalado('pipeline');
  const funis = useQuery({
    queryKey: ['pipeline', 'funis'],
    queryFn: () => get<{ itens: { id: string; nome: string }[] }>('/pipeline/funis'),
    enabled: comPipeline && s.pode('pipeline.ver'),
  });
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  useValidacaoLocal(erros, erroGeral);
  const usuarios = useQuery({ queryKey: ['conversas', 'membros-workspace'], queryFn: () => getTodos<Membro>('/membros') });
  const presenca = new Map((fila?.membros ?? []).map((m) => [m.usuario_id, m]));

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = {
        nome: nome.trim(),
        distribuicao,
        canais,
        membros,
        ativa,
        limite_por_atendente: limite.trim() ? Number(limite) : null,
        horario: usarHorario ? horario : null,
        mensagem_fora_horario: mensagemFora.trim() || null,
        ...(comPipeline || fila?.oportunidade_automatica ? { oportunidade_automatica: oportunidadeAuto, oportunidade_funil_id: oportunidadeAuto && funilOp ? funilOp : null } : {}),
      };
      return fila ? patch(`/conversas/filas/${fila.id}`, corpo) : post('/conversas/filas', corpo);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/conversas/filas'] });
      await qc.invalidateQueries({ queryKey: ['conversas'] });
      toast.sucesso(tn('comum.salvo'));
      aoConcluir();
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        setErroGeral(Object.keys(porCampo).length ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  const alternar = <T,>(lista: T[], v: T, marcado: boolean) => (marcado ? [...lista, v] : lista.filter((x) => x !== v));
  return (
    <form
      id="form-fila"
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setErroGeral(null);
        if (!nome.trim()) {
          setErros({ nome: tn('erros.obrigatorioCampo', { campo: t('filas.nome') }) });
          return;
        }
        if (limite.trim() && !(/^\d+$/.test(limite.trim()) && Number(limite) >= 1 && Number(limite) <= 100)) {
          setErros({ limite_por_atendente: tn('erros.valor') });
          return;
        }
        setErros({});
        salvar.mutate();
      }}
    >
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erroGeral}
        </p>
      )}
      <Rotulo id="fila-nome" rotulo={t('filas.nome')} obrigatorio erro={erros.nome}>
        <Entrada id="fila-nome" value={nome} maxLength={80} disabled={!podeEditar} onChange={(e) => setNome(e.target.value)} />
      </Rotulo>
      <Rotulo id="fila-distribuicao" rotulo={t('filas.distribuicao')} ajuda={t('filas.ajudaDistribuicao')} erro={erros.distribuicao}>
        <Selecao
          id="fila-distribuicao"
          disabled={!podeEditar}
          opcoes={(['manual', 'rodizio', 'menor_carga'] as const).map((d) => ({ valor: d, rotulo: t(`filas.distribuicoes.${d}`) }))}
          value={distribuicao}
          onChange={(e) => setDistribuicao(e.target.value as Fila['distribuicao'])}
        />
      </Rotulo>
      {distribuicao === 'menor_carga' && (
        <>
          <p className="text-xs text-tinta-3">{t('t5b.ajudaMenorCarga')}</p>
          <Rotulo id="fila-limite" rotulo={t('t5b.limite')} ajuda={t('t5b.ajudaLimite')} erro={erros.limite_por_atendente}>
            <Entrada id="fila-limite" inputMode="numeric" value={limite} maxLength={3} disabled={!podeEditar} onChange={(e) => setLimite(e.target.value.replace(/\D/g, ''))} />
          </Rotulo>
        </>
      )}
      <fieldset className="flex min-w-0 flex-col gap-1.5">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('filas.canais')}</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {CANAIS.map((c) => (
            <label key={c} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm">
              <input type="checkbox" disabled={!podeEditar} checked={canais.includes(c)} onChange={(x) => setCanais((l) => alternar(l, c, x.target.checked))} />
              <span className="min-w-0 truncate" title={t(`w9.canais.${c}`)}>
                {t(`w9.canais.${c}`)}
              </span>
            </label>
          ))}
        </div>
        <span className="text-xs text-tinta-3">{t('filas.ajudaCanais')}</span>
      </fieldset>
      <fieldset className="flex min-w-0 flex-col gap-1.5">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('filas.membros')}</legend>
        {erros.membros && <p className="text-xs text-perigo">{erros.membros}</p>}
        {usuarios.isPending ? (
          <p className="text-sm text-tinta-3">{t('filas.carregandoMembros')}</p>
        ) : (
          <div className="grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2" tabIndex={-1}>
            {(usuarios.data?.itens ?? []).map((u) => (
              <label key={u.id} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm">
                <input type="checkbox" disabled={!podeEditar} checked={membros.includes(u.id)} onChange={(x) => setMembros((l) => alternar(l, u.id, x.target.checked))} />
                <TextoCortado className="min-w-0">{u.nome}</TextoCortado>
                {presenca.get(u.id) && (
                  <span className="ml-auto shrink-0 text-xs text-tinta-3">
                    {presenca.get(u.id)!.disponivel ? `${t('t5b.membroDisponivel')}, ` : ''}
                    {t('t5b.membroCarga', { count: presenca.get(u.id)!.carga ?? 0 })}
                  </span>
                )}
              </label>
            ))}
          </div>
        )}
        <span className="text-xs text-tinta-3">{t('filas.ajudaMembros')}</span>
      </fieldset>
      <fieldset className="flex min-w-0 flex-col gap-2">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('t5b.horario.titulo')}</legend>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
          <label htmlFor="fila-horario" className="text-sm text-tinta-2">
            {t('t5b.horario.usar')}
          </label>
          <Alternador id="fila-horario" rotulo={t('t5b.horario.usar')} marcado={usarHorario} desabilitado={!podeEditar} aoMudar={setUsarHorario} />
        </div>
        <span className="text-xs text-tinta-3">{t('t5b.horario.ajuda')}</span>
        {erros.horario && <p className="text-xs text-perigo">{erros.horario}</p>}
        {usarHorario && <EditorHorario horario={horario} aoMudar={setHorario} desabilitado={!podeEditar} />}
        {usarHorario && (
          <Rotulo id="fila-fora" rotulo={t('t5b.horario.mensagem')} ajuda={t('t5b.horario.ajudaMensagem')} erro={erros.mensagem_fora_horario}>
            <AreaSensivel
              id="fila-fora"
              rotulo={t('t5b.horario.mensagem')}
              rows={3}
              maxLength={1000}
              disabled={!podeEditar}
              value={mensagemFora}
              onChange={(e) => setMensagemFora(e.target.value)}
              className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
            />
          </Rotulo>
        )}
      </fieldset>
      <fieldset className="flex min-w-0 flex-col gap-2">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('traj.oportunidade.titulo')}</legend>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
          <label htmlFor="fila-oportunidade" className="text-sm text-tinta-2">
            {t('traj.oportunidade.usar')}
          </label>
          <Alternador id="fila-oportunidade" rotulo={t('traj.oportunidade.usar')} marcado={oportunidadeAuto} desabilitado={!podeEditar || !comPipeline} aoMudar={setOportunidadeAuto} />
        </div>
        <span className="text-xs text-tinta-3">{comPipeline ? t('traj.oportunidade.ajuda') : t('traj.oportunidade.semPipeline')}</span>
        {erros.oportunidade_automatica && <p className="text-xs text-perigo">{erros.oportunidade_automatica}</p>}
        {oportunidadeAuto && comPipeline && (
          <Rotulo id="fila-funil" rotulo={t('traj.oportunidade.funil')} erro={erros.oportunidade_funil_id}>
            <Selecao
              id="fila-funil"
              disabled={!podeEditar}
              opcoes={[{ valor: '', rotulo: t('traj.oportunidade.funilPadrao') }, ...(funis.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))]}
              value={funilOp}
              onChange={(e) => setFunilOp(e.target.value)}
            />
          </Rotulo>
        )}
      </fieldset>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
        <label htmlFor="fila-ativa" className="text-sm font-medium text-tinta-2">
          {t('filas.ativa')}
        </label>
        <Alternador id="fila-ativa" rotulo={t('filas.ativa')} marcado={ativa} desabilitado={!podeEditar} aoMudar={setAtiva} />
      </div>
      <p className="text-xs text-tinta-3">{t('filas.escopo')}</p>
    </form>
  );
}

export function Filas() {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Fila | 'nova' | null>(null);
  const [remover, setRemover] = useState(false);
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const podeEditar = s.pode('conversas.configurar');

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/conversas/filas/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/conversas/filas'] });
      toast.sucesso(tn('comum.removido'));
      setRemover(false);
      setAberto(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const colunas: Coluna<Fila>[] = [
    {
      chave: 'nome',
      rotulo: t('filas.nome'),
      principal: true,
      render: (f) => (
        <span className="flex min-w-0 items-center gap-2">
          <TextoCortado className="min-w-0 font-medium">{f.nome}</TextoCortado>
          {!f.ativa && <Selo>{t('filas.inativa')}</Selo>}
        </span>
      ),
    },
    { chave: 'distribuicao', rotulo: t('filas.distribuicao'), render: (f) => t(`filas.distribuicoes.${f.distribuicao}`) },
    { chave: 'canais', rotulo: t('filas.canais'), render: (f) => (f.canais.length ? f.canais.map((c) => t(`w9.canais.${c}`)).join(', ') : t('filas.semCanal')) },
    { chave: 'membros', rotulo: t('filas.membros'), render: (f) => (f.membros.length ? <span title={f.membros.map((m) => m.nome).join(', ')}>{t('filas.membrosCount', { count: f.membros.length })}</span> : t('filas.semMembros')) },
    { chave: 'conversas_ativas', rotulo: t('filas.conversasAtivas'), mono: true, render: (f) => <span className="numero">{f.conversas_ativas}</span> },
  ];

  return (
    <>
      {(podeEditar || s.pode('conversas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('conversas.excluir') && <BotaoLixeira entidades={['conversas.fila']} />}
          {podeEditar && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
              {t('filas.nova')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Fila>
        tela="conversas.filas"
        caminho="/conversas/filas"
        colunas={colunas}
        aoClicar={(f) => setAberto(f)}
        vazio={
          <Vazio
            icone={<Users className="size-5" aria-hidden />}
            titulo={t('filas.vazioTitulo')}
            texto={t('filas.vazio')}
            acao={
              podeEditar ? (
                <Botao icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
                  {t('filas.nova')}
                </Botao>
              ) : undefined
            }
          />
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('filas.nova')}
        rodape={
          <>
            {atual && s.pode('conversas.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-fila">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        {aberto && <FormFila key={atual?.id ?? 'nova'} fila={atual} aoConcluir={() => setAberto(null)} />}
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => atual && excluir.mutate(atual.id)} carregando={excluir.isPending} titulo={t('filas.removerTitulo')} texto={t('filas.removerTexto')} perigoso />
    </>
  );
}
